// Stores: what is on the shelves (spares, tyres, batteries, brake linings), issuing to trucks with the old part
// coming back, counting the shelves (differences approved by a manager) and the movements.
import { useState } from "react";
import { Page, Card, Btn, Tabs, Table, Tr, Td, Field, TextIn, Loading, Pill, money } from "@/components/kit";
import api from "@/lib/api";
import { act, useLoad, useCan, Select, DateIn, Num, n, opts } from "@/components/erp";
import { fmtDate, today } from "@/lib/money";

const CATS = [["spares", "Spares"], ["tyres", "Tyres"], ["batteries", "Batteries"], ["brake_linings", "Brake linings"], ["general", "General"]].map(([value, label]) => ({ value, label }));
const TYPE = { receive: "Received", issue: "Issued", count: "Count" };
const TONE = { pending: "amber", approved: "green", rejected: "red" };

export default function Stores() {
  const can = useCan();
  const [tab, setTab] = useState("stock");
  const tabs = [{ value: "stock", label: "Stock" }, { value: "moves", label: "Movements" }, { value: "counts", label: "Counts" }];
  return (
    <Page title="Stores">
      <Tabs value={tab} onChange={setTab} tabs={tabs} />
      {tab === "stock" && <Stock can={can} />}
      {tab === "moves" && <Moves />}
      {tab === "counts" && <Counts can={can} />}
    </Page>
  );
}

// ---------------------------------------------------------------- stock + issue
function Stock({ can }) {
  const { data, reload } = useLoad("/stores/items");
  const trucks = useLoad("/fleet/trucks");
  const [add, setAdd] = useState(null);
  const [iss, setIss] = useState(null);
  if (!data) return <Loading />;
  const value = data.reduce((t, i) => t + i.value, 0);
  const saveItem = async () => {
    if (await act(() => api.post("/stores/items", { ...add, reorder_level: n(add.reorder_level) || 0 }), "Item added")) { setAdd(null); reload(); }
  };
  const issue = async () => {
    const body = { qty: n(iss.qty) || 0, truck_id: iss.truck_id, collected_by: iss.collected_by || "", job_ref: iss.job_ref || "", old_returned: !!iss.old_returned,
      no_old_reason: iss.old_returned ? "" : iss.no_old_reason || "", date: iss.date || today() };
    if (await act(() => api.post(`/stores/items/${iss.item.id}/issue`, body), "Issued")) { setIss(null); reload(); }
  };
  return (
    <>
      <p className="mt-3 text-sm text-slate-600">{data.length} items · stock worth <b>{money(value)}</b></p>
      {can("stores.manage") && !add && <div className="mt-2"><Btn kind="primary" onClick={() => setAdd({ category: "spares", unit: "pcs" })}>+ New item</Btn></div>}
      {add && (
        <Card title="New item" className="mt-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Code"><TextIn value={add.code} onChange={(v) => setAdd({ ...add, code: v })} /></Field>
            <Field label="Name"><TextIn value={add.name} onChange={(v) => setAdd({ ...add, name: v })} /></Field>
            <Field label="What is it"><Select empty={null} value={add.category} options={CATS} onChange={(v) => setAdd({ ...add, category: v })} /></Field>
            <Field label="Unit"><TextIn value={add.unit} onChange={(v) => setAdd({ ...add, unit: v })} /></Field>
            <Field label="Order again when stock falls to"><Num value={add.reorder_level} onChange={(v) => setAdd({ ...add, reorder_level: v })} /></Field>
          </div>
          <p className="mt-2 text-xs text-slate-500">Tyres, batteries and brake linings are issued only when the old part comes back.</p>
          <div className="mt-3 flex gap-2"><Btn kind="primary" onClick={saveItem}>Save</Btn><Btn onClick={() => setAdd(null)}>Cancel</Btn></div>
        </Card>)}
      {iss && (
        <Card title={`Issue ${iss.item.name} (${iss.item.on_hand} ${iss.item.unit} in stock)`} className="mt-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Quantity"><Num value={iss.qty} onChange={(v) => setIss({ ...iss, qty: v })} /></Field>
            <Field label="To which truck"><Select value={iss.truck_id} options={opts(trucks.data, "plate")} onChange={(v) => setIss({ ...iss, truck_id: v })} /></Field>
            <Field label="Collected by"><TextIn value={iss.collected_by} onChange={(v) => setIss({ ...iss, collected_by: v })} /></Field>
            <Field label="Job card / reference"><TextIn value={iss.job_ref} onChange={(v) => setIss({ ...iss, job_ref: v })} /></Field>
            <Field label="Date"><DateIn value={iss.date || today()} onChange={(v) => setIss({ ...iss, date: v })} /></Field>
          </div>
          {iss.item.return_old && (
            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!iss.old_returned} onChange={(e) => setIss({ ...iss, old_returned: e.target.checked })} />The old part has been handed back</label>
              {!iss.old_returned && <div className="mt-2"><Field label="No old part - why? (the manager will see this)"><TextIn value={iss.no_old_reason} onChange={(v) => setIss({ ...iss, no_old_reason: v })} /></Field></div>}
            </div>)}
          <div className="mt-3 flex gap-2"><Btn kind="primary" onClick={issue}>Issue</Btn><Btn onClick={() => setIss(null)}>Cancel</Btn></div>
        </Card>)}
      <Table minWidth={820} cols={[{ label: "Code" }, { label: "Item" }, { label: "Kind" }, { label: "In stock", align: "right" }, { label: "Avg cost", align: "right" }, { label: "Value", align: "right" }, { label: "" }]}>
        {data.map((i) => (
          <Tr key={i.id}><Td className="font-medium">{i.code}</Td><Td>{i.name}</Td><Td>{i.category_label}</Td>
            <Td right>{i.on_hand} {i.unit} {i.low && <Pill kind="red">low</Pill>}</Td><Td right>{money(i.avg_cost)}</Td><Td right>{money(i.value)}</Td>
            <Td>{can("stores.issue") && i.on_hand > 0 && <Btn onClick={() => setIss({ item: i })}>Issue</Btn>}</Td></Tr>))}
      </Table>
      {!data.length && <p className="mt-4 text-sm text-slate-500">No items yet. Add them here, then name them on purchase requests so received goods go onto the shelf.</p>}
    </>
  );
}

// ---------------------------------------------------------------- movements
function Moves() {
  const { data } = useLoad("/stores/moves");
  if (!data) return <Loading />;
  return (
    <>
      <Table minWidth={900} cols={[{ label: "Date" }, { label: "Item" }, { label: "What" }, { label: "Qty", align: "right" }, { label: "Value", align: "right" }, { label: "Truck / ref" }, { label: "By" }]}>
        {data.map((m) => (
          <Tr key={m.id}><Td>{fmtDate(m.date)}</Td><Td>{m.code} · {m.name}</Td><Td>{TYPE[m.type] || m.type}{(m.flags || []).map((f, i) => <div key={i} className="text-xs text-red-600">{f}</div>)}</Td>
            <Td right>{m.qty}</Td><Td right>{money(Math.abs(m.value))}</Td>
            <Td>{m.truck || m.ref}{m.collected_by && <div className="text-xs text-slate-500">{m.collected_by} {m.job_ref}</div>}</Td><Td className="text-xs">{m.by}</Td></Tr>))}
      </Table>
      {!data.length && <p className="mt-4 text-sm text-slate-500">Nothing has moved yet.</p>}
    </>
  );
}

// ---------------------------------------------------------------- counts
function Counts({ can }) {
  const { data, reload } = useLoad("/stores/counts");
  const items = useLoad("/stores/items");
  const [c, setC] = useState(null);
  if (!data || !items.data) return <Loading />;
  const submit = async () => {
    const lines = Object.entries(c.q).filter(([, v]) => v !== "" && v != null).map(([item_id, v]) => ({ item_id, counted: n(v) }));
    if (await act(() => api.post("/stores/counts", { lines, note: c.note || "" }), "Count sent - a manager approves any differences")) { setC(null); reload(); items.reload(); }
  };
  return (
    <>
      {can("stores.count") && !c && <div className="mt-3"><Btn kind="primary" onClick={() => setC({ q: {} })}>+ New count</Btn></div>}
      {c && (
        <Card title="Count the shelves - enter what you find (leave blank what you didn't count)" className="mt-3">
          <Table minWidth={520} cols={[{ label: "Item" }, { label: "Book says", align: "right" }, { label: "Counted", align: "right" }]}>
            {items.data.map((i) => (
              <Tr key={i.id}><Td>{i.code} · {i.name}</Td><Td right>{i.on_hand}</Td>
                <Td right><Num value={c.q[i.id]} onChange={(v) => setC({ ...c, q: { ...c.q, [i.id]: v } })} /></Td></Tr>))}
          </Table>
          <div className="mt-3"><Field label="Note"><TextIn value={c.note} onChange={(v) => setC({ ...c, note: v })} /></Field></div>
          <div className="mt-3 flex gap-2"><Btn kind="primary" onClick={submit}>Send count</Btn><Btn onClick={() => setC(null)}>Cancel</Btn></div>
        </Card>)}
      <Table minWidth={760} cols={[{ label: "Date" }, { label: "Counted by" }, { label: "Differences" }, { label: "Net value", align: "right" }, { label: "Status" }]}>
        {data.map((x) => (
          <Tr key={x.id}><Td>{fmtDate(x.date)}</Td><Td>{x.counted_name}</Td>
            <Td>{x.lines.filter((l) => l.diff).map((l) => `${l.name} ${l.diff > 0 ? "+" : ""}${l.diff}`).join("; ") || "None"}</Td>
            <Td right>{money(x.net_value)}</Td><Td><Pill kind={TONE[x.status] || "grey"}>{x.status}</Pill></Td></Tr>))}
      </Table>
      {!data.length && <p className="mt-4 text-sm text-slate-500">No counts yet.</p>}
    </>
  );
}
